import { ArrowRightIcon, CopyIcon, MapPinIcon, NavigationIcon } from 'lucide-react';
import { toast } from 'sonner';
import { HeadingText } from '../shared/HeadingText';
import { SalonMap } from './SalonMap';
import { hoursLabel, salon } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { isOpen } from '../../core/rules/schedule';
import { salonWeekday } from '../../core/format/dates';
import { useOverlayActions } from '../../contexts/StoreContext';
import { fill } from '../../utils/copy';
import { btnPrimary, btnSecondary } from '../../ui/styles';
import { sectionHeading } from '../../ui/sections';

/** Id van dit blok: de hero linkt hierheen ("Route en kaart"). */
export const VISIT_ID = 'langskomen';

/**
 * Langskomen: alles wat een nieuwe cliënt vóór haar eerste afspraak wil weten.
 * Volgorde = de vragen die ze in die volgorde stelt: waar is het, hoe kom ik er,
 * wanneer kan ik komen, en dan pas: plan een afspraak.
 */
export function ContactVisit() {
  const pages = usePages();
  const {
    openingHours
  } = useSite();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.contact.visit;
  const today = salonWeekday();
  const todayHours = openingHours.find(h => h.weekday === today);
  const address = salon.address || `${salon.street}, ${salon.postalCode} ${salon.locality}`;
  const arrival = [{
    label: 'Openbaar vervoer',
    text: salon.transit
  }, {
    label: 'Parkeren',
    text: salon.parking
  }, {
    label: 'Aanbellen',
    text: salon.entrance
  }, {
    label: 'Toegankelijkheid',
    text: salon.accessibility
  }].filter(a => a.text?.trim());
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success('Adres gekopieerd', {
        description: address
      });
    } catch {
      toast.error('Kopiëren lukte niet', {
        description: address
      });
    }
  };
  return <section id={VISIT_ID} className="scroll-mt-28 bg-blush py-20 sm:py-24 lg:py-32" aria-labelledby="contact-visit" data-id="element-7205" data-mp="1rrc8cuohd3oa">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7206" data-mp="3nw1rztafmqt0">
        {/* Tekst eerst in de bron: op mobiel lees je eerst waar het is, dan zie je de kaart. */}
        <div className="lg:col-span-5 lg:col-start-8 lg:row-start-1" data-id="element-7207" data-mp="1gflxmgb955k">
          {/* Op blush haalt kleine clay-tekst geen AA (4,3:1): het regeltje staat in ink. */}
          {copy.eyebrow && <p className="text-[14px] text-ink" data-id="element-7208" data-mp="bd2423h95i3w">{fill(copy.eyebrow)}</p>}
          <h2 id="contact-visit" className={`mt-4 ${sectionHeading} text-ink`} data-id="element-7209" data-mp="6f0ovtmy62xp">
            <HeadingText heading={copy.heading} data-id="element-7210" data-mp="6h3bs4sszfy7" />
          </h2>
          {copy.body && <p className="mt-6 max-w-md text-[17px] leading-relaxed text-muted" data-id="element-7211" data-mp="kdqmdadoi4ml">{fill(copy.body)}</p>}

          <address className="mt-8 flex gap-3 not-italic" data-id="element-7212" data-mp="fg8957smp38s">
            <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7213" data-mp="2m53eeyujsi18" />
            <div data-id="element-7214" data-mp="2h7v5zpz7bsd6">
              <p className="text-[17px] leading-snug text-ink" data-id="element-7215" data-mp="2h8cx6870bge8">
                {salon.street}
                <br data-id="element-7216" data-mp="wq2iarsjocic" />
                {salon.postalCode} {salon.locality}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-[14px]" data-id="element-7217" data-mp="11mj3bd4g6ar7">
                <a href={salon.routeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 py-1 text-ink underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay hover:underline" data-id="element-7218" data-mp="nx8xyfy8qmxm">
                  <NavigationIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7219" data-mp="iy5ugzxwsorr" />
                  Plan je route<span className="sr-only" data-id="element-7220" data-mp="4zefzcf4x133"> {shopLabels.newTab}</span>
                </a>
                <button type="button" onClick={() => void copyAddress()} className="inline-flex items-center gap-1.5 py-1 text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-7221" data-mp="8zowvk4gtbwq">
                  <CopyIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7222" data-mp="1em1qxedmjx37" />
                  Kopieer adres
                </button>
              </div>
            </div>
          </address>

          {arrival.length > 0 && <div className="mt-10" data-id="element-7223" data-mp="zpb1nxxo01rb">
              {copy.arriveTitle && <h3 className="font-display text-xl text-ink" data-id="element-7224" data-mp="zori6csd95b6">{fill(copy.arriveTitle)}</h3>}
              <dl className="mt-4 divide-y divide-ink/15 border-y border-ink/15" data-id="element-7225" data-mp="vohdr4k82myf">
                {arrival.map(a => <div key={a.label} className="grid gap-1 py-4 sm:grid-cols-[9.5rem_1fr] sm:gap-4" data-id="element-7226" data-mp="qvo9siwkvine">
                    <dt className="text-[14px] text-muted" data-id="element-7227" data-mp="ly602qaqkzec">{a.label}</dt>
                    <dd className="text-[15px] leading-relaxed text-ink" data-id="element-7228" data-mp="h04dvwi0kjyn">{a.text}</dd>
                  </div>)}
              </dl>
            </div>}

          {copy.cta && <div className="mt-8 flex flex-wrap gap-3" data-id="element-7229" data-mp="7zekezg875o3">
              <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnPrimary} data-id="element-7230" data-mp="2jlqjjysd7t5z">
                {fill(copy.cta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7231" data-mp="2nlh61qauzn4p" />
              </button>
              {copy.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noopener noreferrer" className={btnSecondary} data-id="element-7232" data-mp="2xkzmhwrk8qs0">
                  {fill(copy.secondaryCta)}
                  <span className="sr-only" data-id="element-7233" data-mp="1zz292ds1fbmx">{shopLabels.viaWhatsApp}</span>
                </a>}
            </div>}
        </div>

        <div className="lg:col-span-7 lg:col-start-1 lg:row-start-1" data-id="element-7234" data-mp="29v5cro2jfibz">
          <SalonMap loadLabel={fill(copy.mapCta || 'Toon de kaart')} note={fill(copy.mapNote || '')} data-id="element-7235" data-mp="29tu9dpd08cle" />

          <div className="mt-8" data-id="element-7236" data-mp="2eo6rnw2qvhvk">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-7237" data-mp="1h7ro0d8ztt2y">
              <h3 className="font-display text-xl text-ink" data-id="element-7238" data-mp="1m5jbmv2lut1c">Openingstijden · op afspraak</h3>
              {todayHours && <p className="text-[14px] text-muted" data-id="element-7239" data-mp="1e429weliy3y6">
                  Vandaag: <span className="text-ink" data-id="element-7240" data-mp="1a4bd9u9bgtlq">{isOpen(todayHours) ? hoursLabel(todayHours) : 'gesloten'}</span>
                </p>}
            </div>
            {/* Twee kolommen op desktop: zeven regels passen naast elkaar en de kaart blijft het grootst. */}
            <dl className="mt-4 grid border-t border-ink/15 text-[15px] sm:grid-cols-2 sm:gap-x-10" data-id="element-7241" data-mp="1iyfz1z9lfeue">
              {openingHours.map(h => {
              const isToday = h.weekday === today;
              const open = isOpen(h);
              return <div key={h.day} className={`flex justify-between gap-4 border-b border-ink/15 py-3 ${isToday ? 'font-medium' : ''}`} aria-current={isToday ? 'date' : undefined} data-id="element-7242" data-mp="155hxtwagysix">
                    <dt className={open || isToday ? 'text-ink' : 'text-muted'} data-id="element-7243" data-mp="2bl0y7lwb8v8i">
                      {h.day}
                      {isToday && <span className="ml-2 text-[13px] font-normal text-ink underline underline-offset-4" data-id="element-7244" data-mp="3thcgv7yvvd7a">vandaag</span>}
                    </dt>
                    <dd className={`tabular-nums ${open ? 'text-ink' : 'text-muted'}`} data-id="element-7245" data-mp="29q8j217ymk3">{hoursLabel(h)}</dd>
                  </div>;
            })}
            </dl>
            <p className="mt-4 text-[13px] leading-relaxed text-muted" data-id="element-7246" data-mp="3ph2dm32h70w4">
              De webshop is altijd open. {salon.name} · KvK {salon.kvk}
              {salon.vatId && <> · Btw-id {salon.vatId}</>}
            </p>
          </div>
        </div>
      </div>
    </section>;
}